import { handleUDL } from "@utils/UDL";
// ─── Constants ────────────────────────────────────────────────────────────────

const CLASSES = {
  open: "is-open",
};

const INIT_ATTR = "data-enh-nav-dropdown-initialized";

const SELECTORS = {
  metaNavigationButton: ".dropdown-linklist__active-page",
  dropdownLinkItem: ".dropdown-linklist__link-item",
};

// ─── Class ────────────────────────────────────────────────────────────────────

export class EnhancedDropDownLinkList {
  private el: HTMLElement;
  private abortController: AbortController;

  constructor(el: HTMLElement) {
    this.el = el;
    this.abortController = new AbortController();
  }

  init(): void {
    this.bindEvents();
  }

  destroy(): void {
    this.abortController.abort();
  }

  // ─── Event binding ──────────────────────────────────────────────────────────

  private bindEvents(): void {
    const { signal } = this.abortController;

    // Toggle open/closed on button click
    this.el
      .querySelector(SELECTORS.metaNavigationButton)
      ?.addEventListener(
        "click",
        () => this.el.classList.toggle(CLASSES.open),
        { signal },
      );

    // Close when focus leaves the component (event delegation)
    this.el.addEventListener(
      "focusout",
      (e: FocusEvent) => {
        if (!this.el.contains(e.relatedTarget as Node)) {
          this.el.classList.remove(CLASSES.open);
        }
      },
      { signal },
    );

    // Close when clicking outside
    window.addEventListener(
      "click",
      (e: MouseEvent) => {
        if (
          this.el.classList.contains(CLASSES.open) &&
          !this.el.contains(e.target as Node)
        ) {
          this.el.classList.remove(CLASSES.open);
        }
      },
      { signal },
    );

    // UDL tracking on dropdown link item clicks
    this.el.querySelectorAll<HTMLElement>(SELECTORS.dropdownLinkItem).forEach((linkItem) => {
      linkItem.addEventListener(
        "click",
        () => {
          const link_text = linkItem.innerText;
          const link_url = linkItem.querySelector("a")?.href ?? "";
          handleUDL("meta_navigation", link_text, link_url);
        },
        { signal },
      );
    });
  }

  // ─── Static bootstrap ───────────────────────────────────────────────────────

  static initAll(root: Document | HTMLElement = document): void {
    root
      .querySelectorAll<HTMLElement>('[data-component="enhanced-dropdown-linklist"]')
      .forEach((el) => {
        if (el.hasAttribute(INIT_ATTR)) return;
        el.setAttribute(INIT_ATTR, "true");
        new EnhancedDropDownLinkList(el).init();
      });
  }
}
